import React from 'react';

import autoBind from 'react-autobind';
import Switch, { Case } from 'react-switch-case';

// import other components within the same project here
import Config from "../../Config.jsx";

import Localization from "../../Localization.jsx"

import ContentDetailView from "./ContentDetailView.jsx"

import { Stepper, SimpleGrid, Modal, Button, Text, Box, Divider, Stack, Group, UnstyledButton } from '@mantine/core';
import { ContentTypeIcon } from "./contentTypeIcon.jsx";

import { BRAND_PRIMARY } from "../../theme/brandTheme.js";

const STEP_CONTENT_TYPE = 0;
const STEP_EDIT_CONTENT = 1;
const PRIVILEGE_NPC_CONTENT = 'AI_NPC_CONTENT_TYPE';
const PRIVILEGE_3D_MODEL = 'AI_3D_MODEL';

class CreateContentDialog extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      open: false,
      activeStep: STEP_CONTENT_TYPE,
      contentTypeArray: [],
      currentContentType: -1,
      npcQuota: null, // { used, limit } for the current locale; only set after the NPC quota check passes
    };
    autoBind(this);

    for (var i = 0; i < this.props.parent.getContentTypeCount(); i++) {
      this.state.contentTypeArray.push(null);
    }
  }

  isPrivilegeEnabled(privilegeName) {
    if (!this.props.root || typeof this.props.root.getPrivilege !== 'function') {
      return true;
    }

    const privilege = this.props.root.getPrivilege(privilegeName);

    if (!privilege || !privilege.privilege_name) {
      return true;
    }

    const value = privilege.value;
    if (value === undefined || value === null) {
      return false;
    }

    const normalizedValue = `${value}`.trim().toLowerCase();
    if (normalizedValue === '') {
      return false;
    }

    if (normalizedValue === '1' || normalizedValue === 'true' || normalizedValue === 'yes') {
      return true;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      return numericValue > 0;
    }

    return false;
  }

  shouldDisplayContentType(kind) {
    if (kind === Config.HOTSPOT_CONTENT_NPC) {
      return this.isPrivilegeEnabled(PRIVILEGE_NPC_CONTENT);
    }
    if (kind === Config.HOTSPOT_CONTENT_3D_MODEL) {
      return this.isPrivilegeEnabled(PRIVILEGE_3D_MODEL);
    }
    return true;
  }

  isPresentationContentType(kind) {
    return [
      Config.HOTSPOT_CONTENT_SIMPLE_DESCRIPTION,
      Config.HOTSPOT_CONTENT_VOICE_OVER,
      Config.HOTSPOT_CONTENT_MOVIECLIP,
      Config.HOTSPOT_CONTENT_3D_MODEL,
    ].includes(kind);
  }

  getVisibleContentTypes() {
    let contentTypes = [];
    for (let i = 0; i < this.state.contentTypeArray.length; i++) {
      const kind = i + 1;
      if (!this.shouldDisplayContentType(kind)) {
        continue;
      }
      const cfg = Config.GET_HOTSPOT_CONTENT_CONFIG(kind);
      const hasIcon = cfg.icon && cfg.icon.trim() !== "";
      const hasTitle = cfg.title && cfg.title.trim() !== "";
      if (!hasIcon || !hasTitle) {
        continue;
      }
      contentTypes.push({ kind, cfg });
    }
    return contentTypes;
  }

  renderContentTypeSection(title, description, contentTypes, keyPrefix) {
    if (contentTypes.length === 0) {
      return null;
    }

    return (
      <Box style={{ marginBottom: 32 }}>
        <Text fw={600} size="lg" c={BRAND_PRIMARY} style={{ marginBottom: 4 }}>
          {title}
        </Text>
        <Text size="sm" c="dimmed" style={{ marginBottom: 16 }}>
          {description}
        </Text>
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          {contentTypes.map(({ kind, cfg }) => (
            <UnstyledButton
              key={`${keyPrefix}-${kind}`}
              onClick={()=>this.handleContentTypeClicked(kind)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: 16,
                borderRadius: 8,
                border: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <ContentTypeIcon ligature={cfg.icon} size={40} color="#868e96" stroke={1.5} />
              <div style={{textAlign:'left', padding:'0 0 0 14px'}}>
                <Text fw={700} c={BRAND_PRIMARY}>
                  {cfg.title}
                </Text>
                <Text size="sm" style={{textTransform:'none', color:"#707070"}}>
                  {cfg.description}
                </Text>
              </div>
            </UnstyledButton>
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  handleClose() {
    this.setState({ open: false });
    this.state.open = false;
  }

  handleContentTypeClicked(index) {
    if (!this.shouldDisplayContentType(index)) {
      return;
    }
    // 3D model: NO quota gate here. Opening the panel costs nothing — the
    // teacher can read the settings, the (i) explanations and the quota badge
    // before committing — and a quota check that fails (or a school whose
    // subscription row cannot be read) used to make the card look dead: one
    // dialog, then back to square one. The spend happens on 生成3D模型, and
    // that is where the block lives now (ContentDetailView.handleStart3DClicked,
    // backed by the server's own refusal at submit time).

    // For NPC, enforce the per-locale NPC limit (system_config: npc_limit_per_locale).
    if (index === Config.HOTSPOT_CONTENT_NPC) {
      const currentLocale = this.props.root && typeof this.props.root.getCurrentLocale === 'function'
        ? this.props.root.getCurrentLocale()
        : null;
      const epackId = currentLocale ? currentLocale.id : null;

      const proceedToEdit = (quota) => {
        this.setState({ npcQuota: quota, currentContentType: index });
        this.state.npcQuota = quota;
        this.state.currentContentType = index;
        this.changeActiveStep(STEP_EDIT_CONTENT);
      };

      if (!epackId) {
        // No locale context to count against; allow creation without a quota badge.
        proceedToEdit(null);
        return;
      }

      this.props.root.refs.API.request(
        "get_npc_limit_per_locale",
        { epack_id: epackId },
        (res) => {
          const data = res && res.body ? (res.body.result || res.body) : {};
          const limit = parseInt(data.npc_limit_per_locale, 10);
          const used = parseInt(data.used, 10) || 0;

          // A non-positive / unparseable limit means "not configured" -> do not gate.
          if (!Number.isFinite(limit) || limit <= 0) {
            proceedToEdit(null);
            return;
          }

          if (used >= limit) {
            const message = Localization.strings.formatString(
              Localization.strings.NPC_LIMIT_REACHED_MESSAGE, used, limit
            );
            const confirmDialog = this.props.root && this.props.root.refs ? this.props.root.refs.confirmDialog : null;
            if (confirmDialog && typeof confirmDialog.show === 'function') {
              confirmDialog.show(
                {
                  title: Localization.strings.NPC_LIMIT_REACHED_TITLE,
                  content: message,
                  confirm: Localization.strings.OK || 'OK',
                },
                () => {},
                null
              );
            } else if (this.props.root && typeof this.props.root.addNotification === 'function') {
              this.props.root.addNotification(message, false);
            }
            // Limit reached: do NOT open the Create NPC panel.
            return;
          }

          proceedToEdit({ used, limit });
        },
        (err) => {
          console.log('get_npc_limit_per_locale failed', err);
          // On failure, fall back to allowing creation without a quota badge.
          proceedToEdit(null);
        }
      );
      return;
    }

    this.setState({ currentContentType: index });
    this.state.currentContentType = index;
    this.changeActiveStep(STEP_EDIT_CONTENT);
  }

  handleBackClicked() {
    this.changeActiveStep(STEP_CONTENT_TYPE);
  }

  getFinishButtonText() {
    // For 3D model (kind=16), show "更新" if component already created, otherwise "完成"
    if (this.state.currentContentType === Config.HOTSPOT_CONTENT_3D_MODEL) {
      const contentDetailViewState = this.refs.contentDetailView?.state;
      if (contentDetailViewState?.currentContent?.id) {
        return Localization.strings.UPDATE;
      }
    }
    return Localization.strings.FINISH;
  }

  handleFinishClicked() {
    // todo: check empty content
    let maxDisplayIndex = -1;
    for (let i = 0; i < this.props.parent.state.contentList.length; i++) {
      let displayIndex = parseInt(this.props.parent.state.contentList[i].display_index);
      if (displayIndex > maxDisplayIndex) {
        maxDisplayIndex = displayIndex;
      }
    }
    maxDisplayIndex++;

    let contentDetailViewState = this.refs.contentDetailView.state;
    let contentDetailView = this.refs.contentDetailView;
    let return_message = contentDetailView.checkEmptyContent();
    // console.log('return message:', return_message);
    // console.log('content:', contentDetailViewState.contentText);
    // console.log('payload:', contentDetailViewState.contentPayload);
    // if (contentDetailViewState.contentText == "" && Object.keys(contentDetailViewState.contentPayload).length == 0){

    // A kind=16 component row is created by generate3DModel and nothing else:
    // the row and its ai_3d_gen_item job record are born together. Saving one
    // from here would leave a component with an image and NO gen item — and
    // the server's ADD cron discovers exactly that shape and generates it with
    // default parameters, which is the auto-start the 生成3D模型 button exists
    // to replace. So a 3D component with no generation behind it is not saved:
    // press start first (the dialog stays open, and the panel keeps its state).
    if (this.state.currentContentType === Config.HOTSPOT_CONTENT_3D_MODEL &&
        !(contentDetailViewState.currentContent && contentDetailViewState.currentContent.id)) {
      this.props.root.addNotification(
        Localization.strings.GEN3D_START_REQUIRED || 'Press "Generate 3D Model" to start the generation before saving this component.',
        false,
      );
      return;
    }

    // For 3D Model (kind = 16), if component already created (has ID), update it instead of creating new
    if (this.state.currentContentType === Config.HOTSPOT_CONTENT_3D_MODEL &&
        contentDetailViewState.currentContent &&
        contentDetailViewState.currentContent.id) {
      // Get the current contentName from the component's state
      let finalContentName = contentDetailViewState.contentName || "";
      
      // Set default contentName if empty
      if (finalContentName.trim() === "") {
        finalContentName = Localization.strings.MODEL_3D_DEFAULT_NAME || "3D Model";
      }
      
      console.log("Updating 3D Model component:", {
        id: contentDetailViewState.currentContent.id,
        name: finalContentName,
        originalName: contentDetailViewState.contentName,
        stateSnapshot: JSON.stringify(contentDetailViewState.contentName)
      });
      
      // Update existing component - must use where_field and where_value for UPDATE to work
      let updateRequestObj = {
        table: "component",
        where_field: "id",
        where_value: contentDetailViewState.currentContent.id,
        row: {
          name: finalContentName,
          diff: contentDetailViewState.contentDifficulty,
          content: contentDetailViewState.contentText,
          payload: this.refs.contentDetailView.serializePayload(),
          score_max: (typeof contentDetailViewState.scoreMaxUi === 'number' ? contentDetailViewState.scoreMaxUi : -1),
        }
      };
      console.log("Update request payload:", JSON.stringify(updateRequestObj, null, 2));
      this.props.root.refs.API.request("update", updateRequestObj, (res) => {
        console.log("Update API response:", res);
        console.log("Update API response body:", res.body);
        
        // Check if update was successful
        if (res.body && res.body.count && res.body.count > 0) {
          console.log(`✅ Successfully updated ${res.body.count} row(s)`);
          this.props.root.addNotification(`成功更新: ${finalContentName}`, true);
        } else {
          console.warn("⚠️ Update returned success but count is 0 - no rows updated");
          console.log("SQL query:", res.body ? res.body.sql_stmt : "No SQL in response");
        }
        
        this.props.parent.refreshContentList();
        var requestObj = {epack_id: this.props.root.getCurrentLocale().id};
        this.props.root.refs.API.request("touch_epack", requestObj);
        this.handleClose();
      }, (err) => {
        console.log("update content fail", err);
        this.props.root.addNotification("更新失敗", false);
        this.handleClose();
      });
      return;
    }

    switch(return_message.flag){
      case ContentDetailView.TAG_OK:
        // save tag normally
        // Set default contentName for kind=16 if empty
      let finalName = contentDetailViewState.contentName;
        if (this.state.currentContentType === Config.HOTSPOT_CONTENT_3D_MODEL && (!finalName || finalName.trim() === "")) {
          finalName = Localization.strings.MODEL_3D_DEFAULT_NAME || "3D Model";
        }
        
        let requestObj = {
          table: "component",
          row: {
            name: finalName,
            kind: this.state.currentContentType,
            display_index: maxDisplayIndex,
            diff: contentDetailViewState.contentDifficulty,
            epack_id: this.props.root.getCurrentLocale().id,
            hotspot_id: this.props.parent.props.currentHotspot.id,
            content: contentDetailViewState.contentText,
            payload: this.refs.contentDetailView.serializePayload(),
            score_max: (typeof contentDetailViewState.scoreMaxUi === 'number' ? contentDetailViewState.scoreMaxUi : -1),
          }
        }
        this.props.root.refs.API.request("insert", requestObj, this.createContentOK, this.createContentFail);
        this.handleClose();
        break;

      case ContentDetailView.TAG_WARNING:
        // prompt user
        let dialogText = {
          title: Localization.strings.EXIT,
          content: return_message.message,
          confirm: Localization.strings.YES,
        };
        this.props.root.refs.confirmDialog.show(dialogText, ()=>{
          // Set default contentName for kind=16 if empty
          let finalNameWarning = contentDetailViewState.contentName;
          if (this.state.currentContentType === Config.HOTSPOT_CONTENT_3D_MODEL && (!finalNameWarning || finalNameWarning.trim() === "")) {
            finalNameWarning = Localization.strings.MODEL_3D_DEFAULT_NAME || "3D Model";
          }
          
          let requestObj = {
            table: "component",
            row: {
              name: finalNameWarning,
              kind: this.state.currentContentType,
              display_index: maxDisplayIndex,
              diff: contentDetailViewState.contentDifficulty,
              epack_id: this.props.root.getCurrentLocale().id,
              hotspot_id: this.props.parent.props.currentHotspot.id,
              content: contentDetailViewState.contentText,
              payload: this.refs.contentDetailView.serializePayload(),
              score_max: (typeof contentDetailViewState.scoreMaxUi === 'number' ? contentDetailViewState.scoreMaxUi : -1),
            }
          }
          this.props.root.refs.API.request("insert", requestObj, this.createContentOK, this.createContentFail);
          this.handleClose();
        }, ()=>{});
        break;

      case ContentDetailView.TAG_ERROR:
        // block user from saving this problematic tag
        this.props.root.addNotification(return_message.message, false);
        break;
    }
  }

  createContentOK(res) {
    this.props.parent.refreshContentList();

    var requestObj = {epack_id: this.props.root.getCurrentLocale().id};
    this.props.root.refs.API.request("touch_epack", requestObj);
  }

  createContentFail(err) {
    console.log("create content fail " + err);
  }

  changeActiveStep(i) {
    this.setState({activeStep: i});
    this.state.activeStep = i;
  }

  render() {
    const visibleContentTypes = this.getVisibleContentTypes();
    const presentationContentTypes = visibleContentTypes.filter(({ kind }) => this.isPresentationContentType(kind));
    const studentResponseContentTypes = visibleContentTypes.filter(({ kind }) => !this.isPresentationContentType(kind));

    return (
      <div>
        <Modal
          opened={this.state.open}
          onClose={this.handleClose}
          title={<Text fw={600}>{Localization.strings.CREATE_CONTENT || Localization.strings.CONTENT_TYPE}</Text>}
          size="xl"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Stepper active={this.state.activeStep}>
              <Stepper.Step label={Localization.strings.CONTENT_TYPE} />
              <Stepper.Step label={Localization.strings.EDIT_CONTENT} />
            </Stepper>

            <Switch condition={this.state.activeStep}>
              <Case value={STEP_CONTENT_TYPE}>
                <Box>
                  {this.renderContentTypeSection(
                    Localization.strings.CONTENT_TYPE_GROUP_PRESENTATION,
                    Localization.strings.CONTENT_TYPE_GROUP_PRESENTATION_DESCRIPTION,
                    presentationContentTypes,
                    "presentation"
                  )}
                  <Divider my="lg" />
                  {this.renderContentTypeSection(
                    Localization.strings.CONTENT_TYPE_GROUP_RESPONSE,
                    Localization.strings.CONTENT_TYPE_GROUP_RESPONSE_DESCRIPTION,
                    studentResponseContentTypes,
                    "response"
                  )}
                </Box>
              </Case>
              <Case value={STEP_EDIT_CONTENT}>
                <ContentDetailView key={this.state.currentContentType} root={this.props.root} parent={this} ref="contentDetailView" contentType={this.state.currentContentType} npcQuota={this.state.npcQuota}/>
              </Case>
            </Switch>

            <Group justify="flex-end" mt="sm">
              <Switch condition={this.state.activeStep}>
                <Case value={STEP_CONTENT_TYPE}>
                  <Button variant="default" onClick={this.handleClose}>{Localization.strings.CANCEL}</Button>
                </Case>
                <Case value={STEP_EDIT_CONTENT}>
                  <Button variant="default" onClick={this.handleBackClicked}>{Localization.strings.BACK}</Button>
                  <Button
                    onClick={this.handleFinishClicked}
                  >
                    {this.getFinishButtonText()}
                  </Button>
                </Case>
              </Switch>
            </Group>
          </Stack>
        </Modal>
      </div>
    )
  }
}

export default CreateContentDialog;
